import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, ModelObjectWithDirtyCheck, type ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"
import { EnumSalesVisitAssociatedEntityType } from "./enums/enumSalesVisitAssociatedEntityType.generated";
import { EnumTaskRepeatBasis } from "./enums/enumTaskRepeatBasis.generated";

// Model Objects

export class PlannedSalesVisitReadOnly extends ModelObject<PlannedSalesVisitReadOnly> {

    /**
    * Whether this visit fits the routing schedule. Not user editable and only updated by the routing service.
    */
    public doesFitInRoute: boolean = false;
    private _adHocLatitude: number | null = null;
    /**
    * Latitude (if known) (AdHoc only)
    */
    public get adHocLatitude(): number | null { return this._adHocLatitude }
    public set adHocLatitude(value: number | null) {
      this._adHocLatitude = value === undefined ? null : value;
    }
    private _adHocLongitude: number | null = null;
    /**
    * Longitude (if known) (AdHoc only)
    */
    public get adHocLongitude(): number | null { return this._adHocLongitude }
    public set adHocLongitude(value: number | null) {
      this._adHocLongitude = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.doesFitInRoute = jsonSerializedObject.doesFitInRoute;
          this.adHocLatitude = jsonSerializedObject.adHocLatitude == null ? null : jsonSerializedObject.adHocLatitude;
          this.adHocLongitude = jsonSerializedObject.adHocLongitude == null ? null : jsonSerializedObject.adHocLongitude;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.doesFitInRoute = this.doesFitInRoute;
        toRet.adHocLatitude = this.adHocLatitude == null ? null : this.adHocLatitude;
        toRet.adHocLongitude = this.adHocLongitude == null ? null : this.adHocLongitude;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
    ];
}


export class PlannedSalesVisit extends ModelObjectWithDirtyCheck<PlannedSalesVisit> {

    public id: Guid = Guid.createEmpty();
    public version: number = 0;
    /**
    * User to which this sales visit is assigned.
    */
    public userId: Guid = Guid.createEmpty();
    /**
    * Date of the visit
    */
    public date: LocalDate = LocalDate.ofEpochDay(0);
    /**
    * Planned time of the visit. Either be agreed by the customer or set by the optimiser.
    */
    public plannedTime: LocalTime = LocalTime.MIDNIGHT;
    /**
    * Whether the planned time is agreed by customer (false) or a sequence set by the optimiser (true).
    */
    public isPlannedTimeFlexible: boolean = false;
    /**
    * Duration of this visit in minutes.
    */
    public durationMinutes: number = 0;
    /**
    * Basis for repeating sales visit once completed.
    */
    public repeatBasis: EnumTaskRepeatBasis = EnumTaskRepeatBasis.none;
    /**
    * Parameter for sales visit repeat schedule. Only relevant if RepeatBasis is a basis which uses a parameter.
    */
    public repeatParameter: number = 0;
    /**
    * Notes of this visit.
    * Not Nullable
    * Max Length : 10000
    */
    public notes: string = "";
    /**
    * Entity type associated to this visit.
    */
    public associatedEntityType: EnumSalesVisitAssociatedEntityType = EnumSalesVisitAssociatedEntityType.none;
    private _associatedEntityId: Guid | null = null;
    /**
    * Id of the entity with which the sales visit is associated.
    */
    public get associatedEntityId(): Guid | null { return this._associatedEntityId }
    public set associatedEntityId(value: Guid | null) {
      this._associatedEntityId = value === undefined ? null : value;
    }
    /**
    * Business Name (AdHoc only)
    * Not Nullable
    * Max Length : 1000
    */
    public adHocBusinessName: string = "";
    /**
    * Address 1 (AdHoc only)
    * Not Nullable
    * Max Length : 1000
    */
    public adHocAddressLine1: string = "";
    /**
    * Address 2 (AdHoc only)
    * Not Nullable
    * Max Length : 1000
    */
    public adHocAddressLine2: string = "";
    /**
    * Address 3 (AdHoc only)
    * Not Nullable
    * Max Length : 1000
    */
    public adHocAddressLine3: string = "";
    /**
    * Address 4 (AdHoc only)
    * Not Nullable
    * Max Length : 1000
    */
    public adHocAddressLine4: string = "";
    /**
    * Address 5 (AdHoc only)
    * Not Nullable
    * Max Length : 1000
    */
    public adHocAddressLine5: string = "";
    /**
    * Address 6 (AdHoc only)
    * Not Nullable
    * Max Length : 1000
    */
    public adHocAddressLine6: string = "";
    /**
    * Postcode  (AdHoc only)
    * Not Nullable
    * Max Length : 1000
    */
    public adHocAddressPostcode: string = "";
    public readOnly: PlannedSalesVisitReadOnly = new PlannedSalesVisitReadOnly();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.version = jsonSerializedObject.version;
          this.userId = Guid.parseOrEmpty(jsonSerializedObject.userId);
          this.date = LocalDate.parse(jsonSerializedObject.date);
          this.plannedTime = LocalTime.parse(jsonSerializedObject.plannedTime);
          this.isPlannedTimeFlexible = jsonSerializedObject.isPlannedTimeFlexible;
          this.durationMinutes = jsonSerializedObject.durationMinutes;
          this.repeatBasis = EnumTaskRepeatBasis.getByValue(jsonSerializedObject.repeatBasis, this.repeatBasis);
          this.repeatParameter = jsonSerializedObject.repeatParameter;
          this.notes = jsonSerializedObject.notes;
          this.associatedEntityType = EnumSalesVisitAssociatedEntityType.getByValue(jsonSerializedObject.associatedEntityType, this.associatedEntityType);
          this.associatedEntityId = jsonSerializedObject.associatedEntityId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.associatedEntityId);
          this.adHocBusinessName = jsonSerializedObject.adHocBusinessName;
          this.adHocAddressLine1 = jsonSerializedObject.adHocAddressLine1;
          this.adHocAddressLine2 = jsonSerializedObject.adHocAddressLine2;
          this.adHocAddressLine3 = jsonSerializedObject.adHocAddressLine3;
          this.adHocAddressLine4 = jsonSerializedObject.adHocAddressLine4;
          this.adHocAddressLine5 = jsonSerializedObject.adHocAddressLine5;
          this.adHocAddressLine6 = jsonSerializedObject.adHocAddressLine6;
          this.adHocAddressPostcode = jsonSerializedObject.adHocAddressPostcode;
          this.readOnly = new PlannedSalesVisitReadOnly(jsonSerializedObject.readOnly);
        }

        this.setDirtyCheckpoint();
    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.id = this.id.toString();
        toRet.version = this.version;
        toRet.userId = this.userId.toString();
        toRet.date = this.date.toString();
        toRet.plannedTime = this.plannedTime.toString();
        toRet.isPlannedTimeFlexible = this.isPlannedTimeFlexible;
        toRet.durationMinutes = this.durationMinutes;
        toRet.repeatBasis = this.repeatBasis.value;
        toRet.repeatParameter = this.repeatParameter;
        toRet.notes = this.notes;
        toRet.associatedEntityType = this.associatedEntityType.value;
        toRet.associatedEntityId = this.associatedEntityId == null ? null : this.associatedEntityId.toString();
        toRet.adHocBusinessName = this.adHocBusinessName;
        toRet.adHocAddressLine1 = this.adHocAddressLine1;
        toRet.adHocAddressLine2 = this.adHocAddressLine2;
        toRet.adHocAddressLine3 = this.adHocAddressLine3;
        toRet.adHocAddressLine4 = this.adHocAddressLine4;
        toRet.adHocAddressLine5 = this.adHocAddressLine5;
        toRet.adHocAddressLine6 = this.adHocAddressLine6;
        toRet.adHocAddressPostcode = this.adHocAddressPostcode;
        toRet.readOnly = this.readOnly.toJsonSerializedObject();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "id", rule: (v: any) => !v.isEmpty() || "PlannedSalesVisit.Id cannot be empty"},
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "userId", rule: (v: any) => !v.isEmpty() || "PlannedSalesVisit.UserId cannot be empty"},
            {field: "durationMinutes", rule: (v: any) => v == null || v >= -2147483648 || "DurationMinutes is not greater than minimum allowed value: -2147483648"},
            {field: "durationMinutes", rule: (v: any) => v == null || v <= 2147483647 || "DurationMinutes is above maximum allowed value: 2147483647"},
            {field: "repeatParameter", rule: (v: any) => v == null || v >= -2147483648 || "RepeatParameter is not greater than minimum allowed value: -2147483648"},
            {field: "repeatParameter", rule: (v: any) => v == null || v <= 2147483647 || "RepeatParameter is above maximum allowed value: 2147483647"},
            {field: "notes", rule: (v: any) => v == null || v.length <= 10000 || "Notes has too many characters (10000)"},
            {field: "associatedEntityId", rule: (v: any) => v == null || !v.isEmpty() || "PlannedSalesVisit.AssociatedEntityId cannot be empty"},
            {field: "adHocBusinessName", rule: (v: any) => v == null || v.length <= 1000 || "AdHocBusinessName has too many characters (1000)"},
            {field: "adHocAddressLine1", rule: (v: any) => v == null || v.length <= 1000 || "AdHocAddressLine1 has too many characters (1000)"},
            {field: "adHocAddressLine2", rule: (v: any) => v == null || v.length <= 1000 || "AdHocAddressLine2 has too many characters (1000)"},
            {field: "adHocAddressLine3", rule: (v: any) => v == null || v.length <= 1000 || "AdHocAddressLine3 has too many characters (1000)"},
            {field: "adHocAddressLine4", rule: (v: any) => v == null || v.length <= 1000 || "AdHocAddressLine4 has too many characters (1000)"},
            {field: "adHocAddressLine5", rule: (v: any) => v == null || v.length <= 1000 || "AdHocAddressLine5 has too many characters (1000)"},
            {field: "adHocAddressLine6", rule: (v: any) => v == null || v.length <= 1000 || "AdHocAddressLine6 has too many characters (1000)"},
            {field: "adHocAddressPostcode", rule: (v: any) => v == null || v.length <= 1000 || "AdHocAddressPostcode has too many characters (1000)"},
            {field: "readOnly", rule: (v: any) => !!v || "readOnly is required"},
    ];
}


export class SalesVisitAssociatedEntityFilter extends ModelObject<SalesVisitAssociatedEntityFilter> {

    public entityType: EnumSalesVisitAssociatedEntityType = EnumSalesVisitAssociatedEntityType.none;
    public entityId: Guid = Guid.createEmpty();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.entityType = EnumSalesVisitAssociatedEntityType.getByValue(jsonSerializedObject.entityType, this.entityType);
          this.entityId = Guid.parseOrEmpty(jsonSerializedObject.entityId);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.entityType = this.entityType.value;
        toRet.entityId = this.entityId.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "entityId", rule: (v: any) => !v.isEmpty() || "SalesVisitAssociatedEntityFilter.EntityId cannot be empty"},
    ];
}


export class PlannedSalesVisitFilter extends ModelObject<PlannedSalesVisitFilter> {

    private _maximumResults: number | null = null;
    public get maximumResults(): number | null { return this._maximumResults }
    public set maximumResults(value: number | null) {
      this._maximumResults = value === undefined ? null : value;
    }
    private _limitUserIds: readonly (Guid)[] | null = null;
    public get limitUserIds(): readonly (Guid)[] | null { return this._limitUserIds }
    public set limitUserIds(value: readonly (Guid)[] | null) {
      this._limitUserIds = value === undefined ? null : value;
    }
    private _fromDate: LocalDate | null = null;
    /**
    * Minimum (inclusive) date.
    */
    public get fromDate(): LocalDate | null { return this._fromDate }
    public set fromDate(value: LocalDate | null) {
      this._fromDate = value === undefined ? null : value;
    }
    private _toDate: LocalDate | null = null;
    /**
    * Maximum (inclusive) date.
    */
    public get toDate(): LocalDate | null { return this._toDate }
    public set toDate(value: LocalDate | null) {
      this._toDate = value === undefined ? null : value;
    }
    private _limitAssociatedEntities: readonly (SalesVisitAssociatedEntityFilter)[] | null = null;
    public get limitAssociatedEntities(): readonly (SalesVisitAssociatedEntityFilter)[] | null { return this._limitAssociatedEntities }
    public set limitAssociatedEntities(value: readonly (SalesVisitAssociatedEntityFilter)[] | null) {
      this._limitAssociatedEntities = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.maximumResults = jsonSerializedObject.maximumResults == null ? null : jsonSerializedObject.maximumResults;
          this.limitUserIds = jsonSerializedObject.limitUserIds == null ? null : jsonSerializedObject.limitUserIds.map((v: any) => Guid.parseOrEmpty(v));
          this.fromDate = jsonSerializedObject.fromDate == null ? null : LocalDate.parse(jsonSerializedObject.fromDate);
          this.toDate = jsonSerializedObject.toDate == null ? null : LocalDate.parse(jsonSerializedObject.toDate);
          this.limitAssociatedEntities = jsonSerializedObject.limitAssociatedEntities == null ? null : jsonSerializedObject.limitAssociatedEntities.map((v: any) => new SalesVisitAssociatedEntityFilter(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.maximumResults = this.maximumResults == null ? null : this.maximumResults;
        toRet.limitUserIds = this.limitUserIds == null ? null : this.limitUserIds.map(v => v.toString());
        toRet.fromDate = this.fromDate == null ? null : this.fromDate.toString();
        toRet.toDate = this.toDate == null ? null : this.toDate.toString();
        toRet.limitAssociatedEntities = this.limitAssociatedEntities == null ? null : this.limitAssociatedEntities.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "maximumResults", rule: (v: any) => v == null || v >= 0 || "MaximumResults is not greater than minimum allowed value: 0"},
            {field: "maximumResults", rule: (v: any) => v == null || v <= 2147483647 || "MaximumResults is above maximum allowed value: 2147483647"},
            {field: "limitAssociatedEntities", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as SalesVisitAssociatedEntityFilter[]).reduce((acc: Array<string>, curr: SalesVisitAssociatedEntityFilter) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class FilteredPlannedSalesVisitResult extends ModelObject<FilteredPlannedSalesVisitResult> {

    public matchingCount: number = 0;
    public plannedSalesVisits: (PlannedSalesVisit)[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.matchingCount = jsonSerializedObject.matchingCount;
          this.plannedSalesVisits = jsonSerializedObject.plannedSalesVisits.map((v: any) => new PlannedSalesVisit(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.matchingCount = this.matchingCount;
        toRet.plannedSalesVisits = this.plannedSalesVisits.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "matchingCount", rule: (v: any) => v == null || v >= -2147483648 || "MatchingCount is not greater than minimum allowed value: -2147483648"},
            {field: "matchingCount", rule: (v: any) => v == null || v <= 2147483647 || "MatchingCount is above maximum allowed value: 2147483647"},
            {field: "plannedSalesVisits", rule: (v: any) => !!v || "plannedSalesVisits is required"},
            {field: "plannedSalesVisits", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as PlannedSalesVisit[]).reduce((acc: Array<string>, curr: PlannedSalesVisit) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class PlannedSalesVisitRouteDetails extends ModelObject<PlannedSalesVisitRouteDetails> {

    public plannedSalesVisitId: Guid = Guid.createEmpty();
    public doesVisitFitInRoute: boolean = false;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.plannedSalesVisitId = Guid.parseOrEmpty(jsonSerializedObject.plannedSalesVisitId);
          this.doesVisitFitInRoute = jsonSerializedObject.doesVisitFitInRoute;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.plannedSalesVisitId = this.plannedSalesVisitId.toString();
        toRet.doesVisitFitInRoute = this.doesVisitFitInRoute;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "plannedSalesVisitId", rule: (v: any) => !v.isEmpty() || "PlannedSalesVisitRouteDetails.PlannedSalesVisitId cannot be empty"},
    ];
}


export class SalesVistLogHeaderReadOnly extends ModelObject<SalesVistLogHeaderReadOnly> {

    /**
    * Timestamp at which this log is created.
    */
    public createdAt: Instant = Instant.ofEpochMilli(0);

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.createdAt = Instant.parse(jsonSerializedObject.createdAt);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.createdAt = this.createdAt.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
    ];
}


export class SalesVisitLogHeader extends ModelObject<SalesVisitLogHeader> {

    public id: Guid = Guid.createEmpty();
    public version: number = 0;
    /**
    * Visit notes
    * Not Nullable
    * Max Length : 10000
    */
    public notes: string = "";
    /**
    * Entity type associated to this visit.
    */
    public associatedEntityType: EnumSalesVisitAssociatedEntityType = EnumSalesVisitAssociatedEntityType.none;
    private _associatedEntityId: Guid | null = null;
    /**
    * Id of the entity with which the sales visit is associated.
    */
    public get associatedEntityId(): Guid | null { return this._associatedEntityId }
    public set associatedEntityId(value: Guid | null) {
      this._associatedEntityId = value === undefined ? null : value;
    }
    /**
    * User who performed this visit.
    */
    public userId: Guid = Guid.createEmpty();
    /**
    * Date/Time of the visit
    */
    public timestamp: Instant = Instant.ofEpochMilli(0);
    /**
    * Business Name (AdHoc only)
    * Not Nullable
    * Max Length : 1000
    */
    public adHocBusinessName: string = "";
    public readOnly: SalesVistLogHeaderReadOnly = new SalesVistLogHeaderReadOnly();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.version = jsonSerializedObject.version;
          this.notes = jsonSerializedObject.notes;
          this.associatedEntityType = EnumSalesVisitAssociatedEntityType.getByValue(jsonSerializedObject.associatedEntityType, this.associatedEntityType);
          this.associatedEntityId = jsonSerializedObject.associatedEntityId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.associatedEntityId);
          this.userId = Guid.parseOrEmpty(jsonSerializedObject.userId);
          this.timestamp = Instant.parse(jsonSerializedObject.timestamp);
          this.adHocBusinessName = jsonSerializedObject.adHocBusinessName;
          this.readOnly = new SalesVistLogHeaderReadOnly(jsonSerializedObject.readOnly);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.id = this.id.toString();
        toRet.version = this.version;
        toRet.notes = this.notes;
        toRet.associatedEntityType = this.associatedEntityType.value;
        toRet.associatedEntityId = this.associatedEntityId == null ? null : this.associatedEntityId.toString();
        toRet.userId = this.userId.toString();
        toRet.timestamp = this.timestamp.toString();
        toRet.adHocBusinessName = this.adHocBusinessName;
        toRet.readOnly = this.readOnly.toJsonSerializedObject();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "id", rule: (v: any) => !v.isEmpty() || "SalesVisitLogHeader.Id cannot be empty"},
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "notes", rule: (v: any) => v == null || v.length <= 10000 || "Notes has too many characters (10000)"},
            {field: "associatedEntityId", rule: (v: any) => v == null || !v.isEmpty() || "SalesVisitLogHeader.AssociatedEntityId cannot be empty"},
            {field: "userId", rule: (v: any) => !v.isEmpty() || "SalesVisitLogHeader.UserId cannot be empty"},
            {field: "adHocBusinessName", rule: (v: any) => v == null || v.length <= 1000 || "AdHocBusinessName has too many characters (1000)"},
            {field: "readOnly", rule: (v: any) => !!v || "readOnly is required"},
    ];
}


export class SalesVistLogSurveryLineReadOnly extends ModelObject<SalesVistLogSurveryLineReadOnly> {

    /**
    * Sales Visit Log Header Id
    */
    public salesVisitLogId: Guid = Guid.createEmpty();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.salesVisitLogId = Guid.parseOrEmpty(jsonSerializedObject.salesVisitLogId);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.salesVisitLogId = this.salesVisitLogId.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "salesVisitLogId", rule: (v: any) => !v.isEmpty() || "SalesVistLogSurveryLineReadOnly.SalesVisitLogId cannot be empty"},
    ];
}


export class SalesVisitLogSurveryLine extends ModelObject<SalesVisitLogSurveryLine> {

    /**
    * The question which was asked.
    */
    public salesQuestionId: Guid = Guid.createEmpty();
    /**
    * Answer, in Text format. Even questions with well-typed AnswerFormat will have their answers stored textually.
    * Not Nullable
    * Max Length : 1000
    */
    public answer: string = "";
    public readOnly: SalesVistLogSurveryLineReadOnly = new SalesVistLogSurveryLineReadOnly();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.salesQuestionId = Guid.parseOrEmpty(jsonSerializedObject.salesQuestionId);
          this.answer = jsonSerializedObject.answer;
          this.readOnly = new SalesVistLogSurveryLineReadOnly(jsonSerializedObject.readOnly);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.salesQuestionId = this.salesQuestionId.toString();
        toRet.answer = this.answer;
        toRet.readOnly = this.readOnly.toJsonSerializedObject();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "salesQuestionId", rule: (v: any) => !v.isEmpty() || "SalesVisitLogSurveryLine.SalesQuestionId cannot be empty"},
            {field: "answer", rule: (v: any) => v == null || v.length <= 1000 || "Answer has too many characters (1000)"},
            {field: "readOnly", rule: (v: any) => !!v || "readOnly is required"},
    ];
}


export class SalesVisitLog extends ModelObjectWithDirtyCheck<SalesVisitLog> {

    public header: SalesVisitLogHeader = new SalesVisitLogHeader();
    public orderIds: (Guid)[] = [];
    public outletCommunicationLogEntryIds: (Guid)[] = [];
    public leadCommunicationLogEntryIds: (Guid)[] = [];
    public surveyLines: (SalesVisitLogSurveryLine)[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.header = new SalesVisitLogHeader(jsonSerializedObject.header);
          this.orderIds = jsonSerializedObject.orderIds.map((v: any) => Guid.parseOrEmpty(v));
          this.outletCommunicationLogEntryIds = jsonSerializedObject.outletCommunicationLogEntryIds.map((v: any) => Guid.parseOrEmpty(v));
          this.leadCommunicationLogEntryIds = jsonSerializedObject.leadCommunicationLogEntryIds.map((v: any) => Guid.parseOrEmpty(v));
          this.surveyLines = jsonSerializedObject.surveyLines.map((v: any) => new SalesVisitLogSurveryLine(v));
        }

        this.setDirtyCheckpoint();
    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.header = this.header.toJsonSerializedObject();
        toRet.orderIds = this.orderIds.map(v => v.toString());
        toRet.outletCommunicationLogEntryIds = this.outletCommunicationLogEntryIds.map(v => v.toString());
        toRet.leadCommunicationLogEntryIds = this.leadCommunicationLogEntryIds.map(v => v.toString());
        toRet.surveyLines = this.surveyLines.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "header", rule: (v: any) => !!v || "header is required"},
            {field: "header", rule: (v: any) => v.validate()},
            {field: "surveyLines", rule: (v: any) => !!v || "surveyLines is required"},
            {field: "surveyLines", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as SalesVisitLogSurveryLine[]).reduce((acc: Array<string>, curr: SalesVisitLogSurveryLine) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class SalesVisitLogFilter extends ModelObject<SalesVisitLogFilter> {

    private _maximumResults: number | null = null;
    public get maximumResults(): number | null { return this._maximumResults }
    public set maximumResults(value: number | null) {
      this._maximumResults = value === undefined ? null : value;
    }
    private _limitUserIds: readonly (Guid)[] | null = null;
    public get limitUserIds(): readonly (Guid)[] | null { return this._limitUserIds }
    public set limitUserIds(value: readonly (Guid)[] | null) {
      this._limitUserIds = value === undefined ? null : value;
    }
    private _fromDate: LocalDate | null = null;
    /**
    * Minimum (inclusive) date.
    */
    public get fromDate(): LocalDate | null { return this._fromDate }
    public set fromDate(value: LocalDate | null) {
      this._fromDate = value === undefined ? null : value;
    }
    private _toDate: LocalDate | null = null;
    /**
    * Maximum (inclusive) date.
    */
    public get toDate(): LocalDate | null { return this._toDate }
    public set toDate(value: LocalDate | null) {
      this._toDate = value === undefined ? null : value;
    }
    private _limitAssociatedEntities: readonly (SalesVisitAssociatedEntityFilter)[] | null = null;
    public get limitAssociatedEntities(): readonly (SalesVisitAssociatedEntityFilter)[] | null { return this._limitAssociatedEntities }
    public set limitAssociatedEntities(value: readonly (SalesVisitAssociatedEntityFilter)[] | null) {
      this._limitAssociatedEntities = value === undefined ? null : value;
    }
    private _orderIds: readonly (Guid)[] | null = null;
    public get orderIds(): readonly (Guid)[] | null { return this._orderIds }
    public set orderIds(value: readonly (Guid)[] | null) {
      this._orderIds = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.maximumResults = jsonSerializedObject.maximumResults == null ? null : jsonSerializedObject.maximumResults;
          this.limitUserIds = jsonSerializedObject.limitUserIds == null ? null : jsonSerializedObject.limitUserIds.map((v: any) => Guid.parseOrEmpty(v));
          this.fromDate = jsonSerializedObject.fromDate == null ? null : LocalDate.parse(jsonSerializedObject.fromDate);
          this.toDate = jsonSerializedObject.toDate == null ? null : LocalDate.parse(jsonSerializedObject.toDate);
          this.limitAssociatedEntities = jsonSerializedObject.limitAssociatedEntities == null ? null : jsonSerializedObject.limitAssociatedEntities.map((v: any) => new SalesVisitAssociatedEntityFilter(v));
          this.orderIds = jsonSerializedObject.orderIds == null ? null : jsonSerializedObject.orderIds.map((v: any) => Guid.parseOrEmpty(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.maximumResults = this.maximumResults == null ? null : this.maximumResults;
        toRet.limitUserIds = this.limitUserIds == null ? null : this.limitUserIds.map(v => v.toString());
        toRet.fromDate = this.fromDate == null ? null : this.fromDate.toString();
        toRet.toDate = this.toDate == null ? null : this.toDate.toString();
        toRet.limitAssociatedEntities = this.limitAssociatedEntities == null ? null : this.limitAssociatedEntities.map(v => v.toJsonSerializedObject());
        toRet.orderIds = this.orderIds == null ? null : this.orderIds.map(v => v.toString());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "maximumResults", rule: (v: any) => v == null || v >= 0 || "MaximumResults is not greater than minimum allowed value: 0"},
            {field: "maximumResults", rule: (v: any) => v == null || v <= 2147483647 || "MaximumResults is above maximum allowed value: 2147483647"},
            {field: "limitAssociatedEntities", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as SalesVisitAssociatedEntityFilter[]).reduce((acc: Array<string>, curr: SalesVisitAssociatedEntityFilter) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class FilteredSalesVisitLogResult extends ModelObject<FilteredSalesVisitLogResult> {

    public matchingCount: number = 0;
    public salesVisitLogs: (SalesVisitLog)[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.matchingCount = jsonSerializedObject.matchingCount;
          this.salesVisitLogs = jsonSerializedObject.salesVisitLogs.map((v: any) => new SalesVisitLog(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.matchingCount = this.matchingCount;
        toRet.salesVisitLogs = this.salesVisitLogs.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "matchingCount", rule: (v: any) => v == null || v >= -2147483648 || "MatchingCount is not greater than minimum allowed value: -2147483648"},
            {field: "matchingCount", rule: (v: any) => v == null || v <= 2147483647 || "MatchingCount is above maximum allowed value: 2147483647"},
            {field: "salesVisitLogs", rule: (v: any) => !!v || "salesVisitLogs is required"},
            {field: "salesVisitLogs", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as SalesVisitLog[]).reduce((acc: Array<string>, curr: SalesVisitLog) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class SalesVisitJourneyCalculated extends ModelObject<SalesVisitJourneyCalculated> {

    public numberOfStops: number = 0;
    public totalDurationSeconds: number = 0;
    public distanceKm: number = 0;
    public polyline: string = "";
    public stopTimeSeconds: number = 0;
    public travelTimeSeconds: number = 0;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.numberOfStops = jsonSerializedObject.numberOfStops;
          this.totalDurationSeconds = jsonSerializedObject.totalDurationSeconds;
          this.distanceKm = jsonSerializedObject.distanceKm;
          this.polyline = jsonSerializedObject.polyline;
          this.stopTimeSeconds = jsonSerializedObject.stopTimeSeconds;
          this.travelTimeSeconds = jsonSerializedObject.travelTimeSeconds;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.numberOfStops = this.numberOfStops;
        toRet.totalDurationSeconds = this.totalDurationSeconds;
        toRet.distanceKm = this.distanceKm;
        toRet.polyline = this.polyline;
        toRet.stopTimeSeconds = this.stopTimeSeconds;
        toRet.travelTimeSeconds = this.travelTimeSeconds;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "numberOfStops", rule: (v: any) => v == null || v >= -2147483648 || "NumberOfStops is not greater than minimum allowed value: -2147483648"},
            {field: "numberOfStops", rule: (v: any) => v == null || v <= 2147483647 || "NumberOfStops is above maximum allowed value: 2147483647"},
    ];
}


export class SalesVisitDetail extends ModelObject<SalesVisitDetail> {

    public salesVisits: (PlannedSalesVisit)[] = [];
    public calculated: SalesVisitJourneyCalculated = new SalesVisitJourneyCalculated();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.salesVisits = jsonSerializedObject.salesVisits.map((v: any) => new PlannedSalesVisit(v));
          this.calculated = new SalesVisitJourneyCalculated(jsonSerializedObject.calculated);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.salesVisits = this.salesVisits.map(v => v.toJsonSerializedObject());
        toRet.calculated = this.calculated.toJsonSerializedObject();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "salesVisits", rule: (v: any) => !!v || "salesVisits is required"},
            {field: "salesVisits", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as PlannedSalesVisit[]).reduce((acc: Array<string>, curr: PlannedSalesVisit) => acc.concat(curr.validate()), new Array<string>());
            }},
            {field: "calculated", rule: (v: any) => !!v || "calculated is required"},
            {field: "calculated", rule: (v: any) => v.validate()},
    ];
}


export class SalesVisitService extends ServiceBase {

  public async getAllPlannedSalesVisits(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(PlannedSalesVisit)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    };
    const extractor = (response:any) => response.plannedSalesVisits.map((v: any) => new PlannedSalesVisit(v));;
    return this.makeJsonRequest("salesVisit/v1/getAllPlannedSalesVisits", req, false, extractor, opts);
  };

  public async getPlannedSalesVisits(plannedSalesVisitIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(PlannedSalesVisit)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    plannedSalesVisitIds:plannedSalesVisitIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.plannedSalesVisits.map((v: any) => new PlannedSalesVisit(v));;
    return this.makeJsonRequest("salesVisit/v1/getPlannedSalesVisits", req, false, extractor, opts);
  };

  public async getPlannedSalesVisit(plannedSalesVisitId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<PlannedSalesVisit> {
    const req = {
    tenantId:api.tenantId.toString(),
    plannedSalesVisitId:plannedSalesVisitId.toString(),
    };
    const extractor = (response:any) => new PlannedSalesVisit(response.plannedSalesVisit);;
    return this.makeJsonRequest("salesVisit/v1/getPlannedSalesVisit", req, false, extractor, opts);
  };

  public async getPlannedSalesVisitsByFilter(filter: PlannedSalesVisitFilter, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<FilteredPlannedSalesVisitResult> {
    const req = {
    tenantId:api.tenantId.toString(),
    filter:filter.toJsonSerializedObject(),
    };
    const extractor = (response:any) => new FilteredPlannedSalesVisitResult(response.results);;
    return this.makeJsonRequest("salesVisit/v1/getPlannedSalesVisitsByFilter", req, false, extractor, opts);
  };

  public async savePlannedSalesVisit(plannedSalesVisit: PlannedSalesVisit, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    plannedSalesVisit:plannedSalesVisit.toJsonSerializedObject(),
    };
    const extractor = null;
    return this.makeJsonRequest("salesVisit/v1/savePlannedSalesVisit", req, true, extractor, opts);
  };

  public async savePlannedSalesVisits(plannedSalesVisits: readonly PlannedSalesVisit[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    plannedSalesVisits:plannedSalesVisits.map(v => v.toJsonSerializedObject()),
    };
    const extractor = null;
    return this.makeJsonRequest("salesVisit/v1/savePlannedSalesVisits", req, true, extractor, opts);
  };

  public async deletePlannedSalesVisit(plannedSalesVisitId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    plannedSalesVisitId:plannedSalesVisitId.toString(),
    };
    const extractor = null;
    return this.makeJsonRequest("salesVisit/v1/deletePlannedSalesVisit", req, true, extractor, opts);
  };

  public async getAllSalesVisitLogs(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(SalesVisitLog)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    };
    const extractor = (response:any) => response.salesVisitLogs.map((v: any) => new SalesVisitLog(v));;
    return this.makeJsonRequest("salesVisit/v1/getAllSalesVisitLogs", req, false, extractor, opts);
  };

  public async getSalesVisitLogs(salesVisitLogIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(SalesVisitLog)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    salesVisitLogIds:salesVisitLogIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.salesVisitLogs.map((v: any) => new SalesVisitLog(v));;
    return this.makeJsonRequest("salesVisit/v1/getSalesVisitLogs", req, false, extractor, opts);
  };

  public async getSalesVisitLog(salesVisitLogId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<SalesVisitLog> {
    const req = {
    tenantId:api.tenantId.toString(),
    salesVisitLogId:salesVisitLogId.toString(),
    };
    const extractor = (response:any) => new SalesVisitLog(response.salesVisitLog);;
    return this.makeJsonRequest("salesVisit/v1/getSalesVisitLog", req, false, extractor, opts);
  };

  public async getSalesVisitLogsByFilter(filter: SalesVisitLogFilter, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<FilteredSalesVisitLogResult> {
    const req = {
    tenantId:api.tenantId.toString(),
    filter:filter.toJsonSerializedObject(),
    };
    const extractor = (response:any) => new FilteredSalesVisitLogResult(response.results);;
    return this.makeJsonRequest("salesVisit/v1/getSalesVisitLogsByFilter", req, false, extractor, opts);
  };

  public async getLastSalesVisitLogPerEntity(associatedEntities: readonly SalesVisitAssociatedEntityFilter[] | null, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(SalesVisitLog)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    associatedEntities:associatedEntities == null ? null : associatedEntities.map(v => v.toJsonSerializedObject()),
    };
    const extractor = (response:any) => response.salesVisitLog.map((v: any) => new SalesVisitLog(v));;
    return this.makeJsonRequest("salesVisit/v1/getLastSalesVisitLogPerEntity", req, false, extractor, opts);
  };

  public async saveSalesVisitLog(salesVisitLog: SalesVisitLog, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    salesVisitLog:salesVisitLog.toJsonSerializedObject(),
    };
    const extractor = null;
    return this.makeJsonRequest("salesVisit/v1/saveSalesVisitLog", req, true, extractor, opts);
  };

  public async deleteSalesVisitLog(salesVisitLogId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    salesVisitLogId:salesVisitLogId.toString(),
    };
    const extractor = null;
    return this.makeJsonRequest("salesVisit/v1/deleteSalesVisitLog", req, true, extractor, opts);
  };

  public async getNextRepeatPlannedDate(plannedSalesVisitId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<LocalDate | null> {
    const req = {
    tenantId:api.tenantId.toString(),
    plannedSalesVisitId:plannedSalesVisitId.toString(),
    };
    const extractor = (response:any) => response.suggestedDate == null ? null : LocalDate.parse(response.suggestedDate);;
    return this.makeJsonRequest("salesVisit/v1/getNextRepeatPlannedDate", req, false, extractor, opts);
  };

  public async getPlannedSalesVisitIfExist(plannedSalesVisitId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<PlannedSalesVisit | null> {
    const req = {
    tenantId:api.tenantId.toString(),
    plannedSalesVisitId:plannedSalesVisitId.toString(),
    };
    const extractor = (response:any) => response.plannedSalesVisit == null ? null : new PlannedSalesVisit(response.plannedSalesVisit);;
    return this.makeJsonRequest("salesVisit/v1/getPlannedSalesVisitIfExist", req, false, extractor, opts);
  };

  public async getSalesVisitsJourneyDetail(salesVisitIds: readonly Guid[], journeyDate: LocalDate, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<SalesVisitDetail> {
    const req = {
    tenantId:api.tenantId.toString(),
    salesVisitIds:salesVisitIds.map(v => v.toString()),
    journeyDate:journeyDate.toString(),
    };
    const extractor = (response:any) => new SalesVisitDetail(response.detail);;
    return this.makeJsonRequest("salesVisit/v1/getSalesVisitsJourneyDetail", req, false, extractor, opts);
  };
}

export const salesVisitService = new SalesVisitService();